// Centralized helpers to keep App.tsx smaller and prevent duplicated toasts.
import { api } from "./api";
import { getAuthToken } from "./auth";

export type NotifyPayload = {
  voidId: string;
  channelId: string;
  authorId?: string;
  authorName?: string;
  content?: string;
  messageId?: string;
  pushSent?: boolean;
};

export type NotificationPreferences = {
  globalMute: boolean;
  perChannelDefault: "all" | "mentions" | "none";
  desktopEnabled: boolean;
  soundEnabled: boolean;
  webPushEnabled: boolean;
  mobilePushEnabled: boolean;
  toneUrl: string | null;
  mutedSpaces: Record<string, boolean>;
  hiddenSpaces: Record<string, boolean>;
};

const DEFAULT_TONE_URL = "/brand/echo_notification.mp3";

export function channelTag(voidId: string, channelId: string): string {
  return channelId.includes(':') ? channelId : `${voidId}:${channelId}`;
}

export function shouldSkipInPageNotification(opts: {
  pushSent?: boolean;
  visibility: DocumentVisibilityState;
  hasFocus: boolean;
}): boolean {
  if (opts.pushSent) return true;
  return false;
}

export function readNotificationPreferences(): NotificationPreferences {
  const settings = readLocalSettings();
  const notifications = settings.notifications || {};
  const perChannelDefault = (
    notifications.perChannelDefault === "all" ||
    notifications.perChannelDefault === "mentions" ||
    notifications.perChannelDefault === "none"
  ) ? notifications.perChannelDefault : "all";

  return {
    globalMute: !!notifications.globalMute,
    perChannelDefault,
    desktopEnabled: readStoredBool("notifEnabled", true),
    soundEnabled: readStoredBool("soundEnabled", true),
    webPushEnabled: readStoredBool("pushEnabled", true),
    mobilePushEnabled: readStoredBool("mobilePushEnabled", true),
    toneUrl: readStoredString("toneUrl", DEFAULT_TONE_URL),
    mutedSpaces: readStoredJsonMap("mutedSpaces"),
    hiddenSpaces: readStoredJsonMap("hiddenSpaces"),
  };
}

export function writeNotificationPreferences(patch: Partial<NotificationPreferences>): void {
  try {
    const settings = readLocalSettings();
    const notifications = settings.notifications && typeof settings.notifications === "object"
      ? settings.notifications
      : {};
    const nextNotifications = { ...notifications };
    if (typeof patch.globalMute === "boolean") nextNotifications.globalMute = patch.globalMute;
    if (patch.perChannelDefault) nextNotifications.perChannelDefault = patch.perChannelDefault;
    settings.notifications = nextNotifications;
    localStorage.setItem("settings", JSON.stringify(settings));

    if (typeof patch.desktopEnabled === "boolean") localStorage.setItem("notifEnabled", patch.desktopEnabled ? "1" : "0");
    if (typeof patch.soundEnabled === "boolean") localStorage.setItem("soundEnabled", patch.soundEnabled ? "1" : "0");
    if (typeof patch.webPushEnabled === "boolean") localStorage.setItem("pushEnabled", patch.webPushEnabled ? "1" : "0");
    if (typeof patch.mobilePushEnabled === "boolean") localStorage.setItem("mobilePushEnabled", patch.mobilePushEnabled ? "1" : "0");
    if ("toneUrl" in patch) {
      if (patch.toneUrl) localStorage.setItem("toneUrl", patch.toneUrl);
      else localStorage.removeItem("toneUrl");
    }
    if (patch.mutedSpaces) localStorage.setItem("mutedSpaces", JSON.stringify(patch.mutedSpaces));
    if (patch.hiddenSpaces) localStorage.setItem("hiddenSpaces", JSON.stringify(patch.hiddenSpaces));
  } catch {
    /* noop */
  }
}

export async function persistNotificationPreferences(patch: Partial<NotificationPreferences>): Promise<void> {
  writeNotificationPreferences(patch);
  const token = getAuthToken();
  if (!token) return;
  try {
    const settingsPatch: Record<string, unknown> = {};
    if (typeof patch.globalMute === "boolean") settingsPatch.globalMute = patch.globalMute;
    if (patch.perChannelDefault) settingsPatch.perChannelDefault = patch.perChannelDefault;
    if (typeof patch.desktopEnabled === "boolean") settingsPatch.desktopEnabled = patch.desktopEnabled;
    if (typeof patch.soundEnabled === "boolean") settingsPatch.soundEnabled = patch.soundEnabled;
    if (typeof patch.webPushEnabled === "boolean") settingsPatch.webPushEnabled = patch.webPushEnabled;
    if (typeof patch.mobilePushEnabled === "boolean") settingsPatch.mobilePushEnabled = patch.mobilePushEnabled;
    if ("toneUrl" in patch) settingsPatch.toneUrl = patch.toneUrl;
    if (patch.mutedSpaces) settingsPatch.mutedSpaces = patch.mutedSpaces;
    if (patch.hiddenSpaces) settingsPatch.hiddenSpaces = patch.hiddenSpaces;
    if (Object.keys(settingsPatch).length === 0) return;
    const res = await api.patchAuth("/users/me/settings", { settings: { notifications: settingsPatch } }, token);
    if (res?.settings?.notifications) {
      const n = res.settings.notifications;
      writeNotificationPreferences({
        globalMute: !!n.globalMute,
        perChannelDefault: n.perChannelDefault,
        desktopEnabled: typeof n.desktopEnabled === "boolean" ? n.desktopEnabled : n.desktop,
        soundEnabled: typeof n.soundEnabled === "boolean" ? n.soundEnabled : undefined,
        webPushEnabled: typeof n.webPushEnabled === "boolean" ? n.webPushEnabled : undefined,
        mobilePushEnabled: typeof n.mobilePushEnabled === "boolean" ? n.mobilePushEnabled : undefined,
        toneUrl: typeof n.toneUrl === "string" || n.toneUrl === null ? n.toneUrl : undefined,
        mutedSpaces: n.mutedSpaces,
        hiddenSpaces: n.hiddenSpaces,
      });
    }
  } catch {
    /* local cache already updated */
  }
}

export async function hydrateServerNotificationPreferences(token: string): Promise<NotificationPreferences> {
  if (!token) return readNotificationPreferences();
  try {
    const res = await api.getAuth("/users/me/settings", token);
    const n = res?.settings?.notifications || {};
    writeNotificationPreferences({
      globalMute: !!n.globalMute,
      perChannelDefault: n.perChannelDefault,
      desktopEnabled: typeof n.desktopEnabled === "boolean" ? n.desktopEnabled : n.desktop,
      soundEnabled: typeof n.soundEnabled === "boolean" ? n.soundEnabled : undefined,
      webPushEnabled: typeof n.webPushEnabled === "boolean" ? n.webPushEnabled : undefined,
      mobilePushEnabled: typeof n.mobilePushEnabled === "boolean" ? n.mobilePushEnabled : undefined,
      toneUrl: typeof n.toneUrl === "string" || n.toneUrl === null ? n.toneUrl : undefined,
      mutedSpaces: n.mutedSpaces,
      hiddenSpaces: n.hiddenSpaces,
    });
  } catch {
    /* keep local cache */
  }
  return readNotificationPreferences();
}

function readStoredJsonMap(key: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

// Page-scoped notifications (created via `new Notification`) are not reachable
// from the service worker's getNotifications(), so we keep our own registry
// keyed by channel. This lets us dismiss them when the conversation is read on
// another device — see closeInPageNotifications.
const inPageNotifications = new Map<string, Set<Notification>>();

export function showInPageNotification(
  title: string,
  body: string,
  tag: string,
  onClick: () => void,
  channelKey?: string
): void {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  const n = new Notification(title, {
    body,
    tag,
    icon: '/brand/ECHO_logo.png',
    badge: '/brand/ECHO_logo.png',
  });
  // Track under the base channel key (DMs use a per-message tag, so the tag
  // alone wouldn't group them) so a single read can clear the whole thread.
  const key = channelKey || tag;
  let set = inPageNotifications.get(key);
  if (!set) { set = new Set(); inPageNotifications.set(key, set); }
  set.add(n);
  const forget = () => { try { set?.delete(n); } catch { /* noop */ } };
  n.onclose = forget;
  n.onclick = () => {
    try { window.focus(); } catch { /* noop */ }
    try { n.close(); } catch { /* noop */ }
    forget();
    onClick();
  };
}

// Dismiss any page-shown notifications for a channel (read elsewhere).
export function closeInPageNotifications(channelKey: string): void {
  if (!channelKey) return;
  const set = inPageNotifications.get(channelKey);
  if (!set) return;
  for (const n of set) { try { n.close(); } catch { /* noop */ } }
  inPageNotifications.delete(channelKey);
}

// Push registration orchestration (throttled) to avoid duplicate token posts.
const PUSH_REGISTER_KEY = 'pushRegisterAt';
const REGISTER_THROTTLE_MS = 10 * 60 * 1000; // 10 minutes, to recover more quickly after background kills.

type RegisterDeps = {
  initPush: (token: string) => Promise<void>;
  registerWebPush: (token: string) => Promise<boolean>;
};

export async function registerPushIfNeeded(token: string, deps: RegisterDeps, force = false): Promise<void> {
  if (!token) return;
  try {
    const raw = localStorage.getItem(PUSH_REGISTER_KEY);
    const last = raw ? Number(raw) : 0;
    const now = Date.now();
    if (!force && last && now - last < REGISTER_THROTTLE_MS) return;
    await deps.initPush(token).catch(() => {});
    await deps.registerWebPush(token).catch(() => {});
    localStorage.setItem(PUSH_REGISTER_KEY, String(now));
  } catch {
    /* noop */
  }
}

function readLocalSettings(): Record<string, any> {
  try {
    const raw = localStorage.getItem("settings");
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function readStoredBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch {
    /* noop */
  }
  return fallback;
}

function readStoredString(key: string, fallback: string | null): string | null {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : raw;
  } catch {
    return fallback;
  }
}
